import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Pencil,
  Plus,
  Trash2,
  Play,
  Pause,
  KeyRound,
  Copy,
  Check,
  X,
} from 'lucide-react';
import { api } from '@/lib/api-client';
import { Select } from '@/components/shared/select';
import { StatusBadge } from '@/components/shared/status-badge';
import type {
  AvailableEntityType,
  AvailableEntityTypesResponse,
  Deployment,
  DeploymentInput,
  DeploymentSecret,
  SwapProfile,
  Model,
} from '@/lib/types';

const ENTITY_TYPE_GROUP_LABEL: Record<AvailableEntityType['group'], string> = {
  presidio_builtin: 'Presidio beépített',
  spacy: 'spaCy NER',
  custom: 'Custom szűrők',
};

const STATUS_TONE = {
  draft: 'slate',
  deployed: 'green',
  disabled: 'amber',
} as const;

function StatusCell({ status }: { status: Deployment['status'] }): JSX.Element {
  return <StatusBadge label={status} tone={STATUS_TONE[status]} />;
}

export function Deployments(): JSX.Element {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Deployment | null>(null);
  const [secret, setSecret] = useState<DeploymentSecret | null>(null);

  const deployments = useQuery({
    queryKey: ['/api/admin/deployments'],
    queryFn: () => api.get<Deployment[]>('/api/admin/deployments'),
  });
  const models = useQuery({
    queryKey: ['/api/admin/models'],
    queryFn: () => api.get<Model[]>('/api/admin/models'),
  });
  const swapProfiles = useQuery({
    queryKey: ['/api/admin/swap-profiles'],
    queryFn: () => api.get<SwapProfile[]>('/api/admin/swap-profiles'),
  });

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: ['/api/admin/deployments'] });
  };

  const create = useMutation({
    // Create returns a plain Deployment (status=draft). The API key is only
    // minted on Deploy, so no secret modal here.
    mutationFn: (v: DeploymentInput) => api.post<Deployment>('/api/admin/deployments', v),
    onSuccess: () => {
      invalidate();
      setShowForm(false);
    },
  });

  const update = useMutation({
    mutationFn: ({ id, v }: { id: string; v: DeploymentInput }) =>
      api.patch<Deployment>(`/api/admin/deployments/${id}`, v),
    onSuccess: () => {
      invalidate();
      setEditing(null);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/deployments/${id}`),
    onSuccess: invalidate,
  });

  const deploy = useMutation({
    // Deploy mints (on first deploy) and returns the API key → show it once.
    mutationFn: (id: string) => api.post<DeploymentSecret>(`/api/admin/deployments/${id}/deploy`),
    onSuccess: (res) => {
      invalidate();
      if (res?.apiKey) setSecret(res);
    },
  });
  const undeploy = useMutation({
    mutationFn: (id: string) => api.post<Deployment>(`/api/admin/deployments/${id}/undeploy`),
    onSuccess: invalidate,
  });
  const rotateKey = useMutation({
    mutationFn: (id: string) => api.post<DeploymentSecret>(`/api/admin/deployments/${id}/rotate-key`),
    onSuccess: (res) => {
      invalidate();
      setSecret(res);
    },
  });

  const modelName = (id: string): string =>
    models.data?.find((m) => m.id === id)?.displayName ?? id;
  const swapProfileName = (id: string): string =>
    swapProfiles.data?.find((p) => p.id === id)?.name ?? id;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900">Deployments</h2>
        <button
          className="btn-primary"
          onClick={() => {
            setEditing(null);
            setShowForm((v) => !v);
          }}
        >
          <Plus className="h-4 w-4" /> {showForm ? 'Bezárás' : 'Új deployment'}
        </button>
      </div>

      {(showForm || editing) && (
        <DeploymentForm
          key={editing?.id ?? 'new'}
          models={models.data ?? []}
          swapProfiles={swapProfiles.data ?? []}
          initial={editing}
          onCancel={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSubmit={(v) => {
            if (editing) update.mutate({ id: editing.id, v });
            else create.mutate(v);
          }}
        />
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Slug</th>
              <th className="px-4 py-2.5">Név</th>
              <th className="px-4 py-2.5">Állapot</th>
              <th className="px-4 py-2.5">Modell</th>
              <th className="px-4 py-2.5">Swap profil</th>
              <th className="px-4 py-2.5" title="OCR jelzők">OCR</th>
              <th className="px-4 py-2.5">Base URL</th>
              <th className="w-40 px-4 py-2.5">Műveletek</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {deployments.isLoading && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-slate-500">Betöltés…</td>
              </tr>
            )}
            {deployments.isError && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-rose-600">
                  Hiba: {String(deployments.error)}
                </td>
              </tr>
            )}
            {deployments.data?.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-slate-500">Nincs deployment.</td>
              </tr>
            )}
            {deployments.data?.map((dep) => (
              <tr key={dep.id} className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{dep.slug}</td>
                <td className="px-4 py-2.5 font-medium">{dep.name}</td>
                <td className="px-4 py-2.5"><StatusCell status={dep.status} /></td>
                <td className="px-4 py-2.5">{dep.modelName ?? modelName(dep.modelId)}</td>
                <td className="px-4 py-2.5">{dep.swapProfileName ?? swapProfileName(dep.swapProfileId)}</td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-1 text-[10px] font-medium">
                    {dep.imageOcrEnabled && (
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-600">IMG</span>
                    )}
                    {dep.pdfOcrEnabled && (
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-600">PDF</span>
                    )}
                    {dep.strictMedia && (
                      <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">STRICT</span>
                    )}
                    {!dep.imageOcrEnabled && !dep.pdfOcrEnabled && !dep.strictMedia && (
                      <span className="text-slate-300">—</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2.5 font-mono text-[11px] text-slate-500">
                  {dep.baseUrl ?? `…/d/${dep.slug}/v1`}
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1">
                    {dep.status === 'deployed' ? (
                      <button
                        title="Undeploy"
                        className="rounded p-1 text-amber-600 hover:bg-amber-50"
                        onClick={() => undeploy.mutate(dep.id)}
                      >
                        <Pause className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        title="Deploy"
                        className="rounded p-1 text-emerald-600 hover:bg-emerald-50"
                        onClick={() => deploy.mutate(dep.id)}
                      >
                        <Play className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      title="API-kulcs cseréje"
                      className="rounded p-1 text-slate-500 hover:bg-slate-100"
                      onClick={() => {
                        if (confirm(`Új API-kulcs generálása ehhez: "${dep.name}"? A régi azonnal érvénytelenné válik.`))
                          rotateKey.mutate(dep.id);
                      }}
                    >
                      <KeyRound className="h-4 w-4" />
                    </button>
                    <button
                      title="Szerkeszt"
                      className="rounded p-1 text-slate-500 hover:bg-slate-100"
                      onClick={() => {
                        setShowForm(false);
                        setEditing(dep);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      title="Töröl"
                      className="rounded p-1 text-rose-500 hover:bg-rose-50"
                      onClick={() => {
                        if (confirm(`Törlés: "${dep.name}"?`)) remove.mutate(dep.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {secret && <SecretModal secret={secret} onClose={() => setSecret(null)} />}
    </div>
  );
}

function DeploymentForm({
  models,
  swapProfiles,
  initial,
  onSubmit,
  onCancel,
}: {
  models: Model[];
  swapProfiles: SwapProfile[];
  initial?: Deployment | null;
  onSubmit: (v: DeploymentInput) => void;
  onCancel: () => void;
}): JSX.Element {
  const isEdit = Boolean(initial);
  const { register, handleSubmit, control, watch, setValue } = useForm<DeploymentInput>({
    defaultValues: initial
      ? {
          name: initial.name,
          modelId: initial.modelId,
          swapProfileId: initial.swapProfileId,
          imageOcrEnabled: initial.imageOcrEnabled,
          pdfOcrEnabled: initial.pdfOcrEnabled,
          strictMedia: initial.strictMedia,
          disabledEntityTypes: initial.disabledEntityTypes ?? [],
        }
      : {
          name: '',
          modelId: models[0]?.id ?? '',
          swapProfileId: swapProfiles.find((p) => p.isDefault)?.id ?? swapProfiles[0]?.id ?? '',
          imageOcrEnabled: false,
          pdfOcrEnabled: false,
          strictMedia: false,
          disabledEntityTypes: [],
        },
  });

  const swapProfileId = watch('swapProfileId');
  const disabledEntityTypes = watch('disabledEntityTypes') ?? [];

  const availableEntityTypes = useQuery({
    queryKey: ['/api/admin/swap-profiles', swapProfileId, 'available-entity-types'],
    queryFn: () =>
      api.get<AvailableEntityTypesResponse>(`/api/admin/swap-profiles/${swapProfileId}/available-entity-types`),
    enabled: Boolean(swapProfileId),
  });

  const toggleEntityType = (label: string): void => {
    const next = disabledEntityTypes.includes(label)
      ? disabledEntityTypes.filter((f) => f !== label)
      : [...disabledEntityTypes, label];
    setValue('disabledEntityTypes', next, { shouldDirty: true });
  };

  const grouped = (availableEntityTypes.data?.entityTypes ?? []).reduce<Record<string, AvailableEntityType[]>>(
    (acc, f) => {
      (acc[f.group] ??= []).push(f);
      return acc;
    },
    {},
  );

  return (
    <form className="card space-y-3 p-4" onSubmit={handleSubmit(onSubmit)}>
      <div className="text-sm font-semibold text-slate-700">
        {isEdit ? `Deployment szerkesztése: ${initial?.name}` : 'Új deployment'}
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div>
          <label className="label">Név</label>
          <input className="input" placeholder="pl. Retail CRM" {...register('name', { required: true })} />
        </div>
        <div>
          <label className="label">Modell</label>
          <Controller
            control={control}
            name="modelId"
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                value={field.value ?? ''}
                onChange={field.onChange}
                placeholder="— válassz modellt —"
                options={models.map((m) => ({ value: m.id, label: m.displayName }))}
              />
            )}
          />
        </div>
        <div>
          <label className="label">Swap profil</label>
          <Controller
            control={control}
            name="swapProfileId"
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                value={field.value ?? ''}
                onChange={field.onChange}
                placeholder="— válassz profilt —"
                options={swapProfiles.map((p) => ({
                  value: p.id,
                  label: p.isDefault ? `${p.name} (alapértelmezett)` : p.name,
                }))}
              />
            )}
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" className="h-4 w-4" {...register('imageOcrEnabled')} />
          Kép OCR engedélyezése
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" className="h-4 w-4" {...register('pdfOcrEnabled')} />
          PDF OCR engedélyezése
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" className="h-4 w-4" {...register('strictMedia')} />
          Szigorú média-szűrés (strict_media)
        </label>
      </div>
      <p className="text-xs text-slate-500">
        Kikapcsolt OCR mellett a melléklet nem jut el a modellhez: a proxy nem olvassa ki a
        tartalmát, ezért nem is küldi ki — a kérésben rövid, állandó jelzőszöveg lép a helyére. A
        két kapcsoló külön dönt a képről és a PDF-ről. Képet vagy PDF-et tartalmazó beszélgetéshez
        kapcsold be.
      </p>
      <p className="text-xs text-slate-500">
        Szigorú média-szűrés bekapcsolva: mindig a kicserélt OCR-szöveg megy a modellnek — az
        eredeti kép/PDF akkor sem, ha nincs benne érzékeny adat. Kikapcsolt OCR mellett nincs
        jelentősége: ott a melléklet amúgy sem megy ki. A kérés egyik esetben sem blokkolódik;
        a feldolgozhatatlan (OCR-hibás) melléklet ettől a kapcsolótól függetlenül kiesik.
      </p>

      {/* Entity type matrix — which entity types this deployment swaps. */}
      <div>
        <label className="label">Aktív entitástípusok (mit cserél ez a deployment)</label>
        {!swapProfileId ? (
          <p className="text-xs text-slate-500">Előbb válassz swap profilt.</p>
        ) : availableEntityTypes.isLoading ? (
          <p className="text-xs text-slate-500">Entitástípusok betöltése…</p>
        ) : (
          <div className="space-y-3">
            {(['presidio_builtin', 'spacy', 'custom'] as const).map((group) => {
              const items = grouped[group] ?? [];
              if (items.length === 0) return null;
              return (
                <div key={group}>
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {ENTITY_TYPE_GROUP_LABEL[group]}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {items.map((f) => {
                      const on = !disabledEntityTypes.includes(f.label);
                      return (
                        <button
                          type="button"
                          key={f.label}
                          onClick={() => toggleEntityType(f.label)}
                          title={f.description ?? f.label}
                          className={
                            on
                              ? 'rounded-md border border-echo-gold bg-echo-gold/10 px-2.5 py-1 text-xs font-medium text-heading'
                              : 'rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground line-through hover:bg-muted'
                          }
                        >
                          {f.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <p className="text-xs text-slate-500">
              Aranyszínű = cserél; áthúzott = ennél a deploymentnél nem cserél
              (a detektálás profil-szinten közös marad).
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onCancel}>Mégse</button>
        <button type="submit" className="btn-primary"><Plus className="h-4 w-4" /> Mentés</button>
      </div>
    </form>
  );
}

function SecretModal({
  secret,
  onClose,
}: {
  secret: DeploymentSecret;
  onClose: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const copy = async (): Promise<void> => {
    await navigator.clipboard.writeText(secret.apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--overlay)' }}
    >
      <div className="card w-full max-w-lg p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-heading">API-kulcs — egyszer látható</h3>
          <button className="rounded p-1 text-slate-400 hover:bg-slate-100" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm text-slate-600">
          Mentsd el most a(z) <strong>{secret.deployment.name}</strong> deployment API-kulcsát.
          Bezárás után többé nem jeleníthető meg.
        </p>
        <div className="mt-3 space-y-2">
          <div>
            <label className="label text-xs">Base URL</label>
            <code className="block break-all rounded-md bg-slate-100 px-3 py-2 font-mono text-xs text-slate-700">
              {secret.deployment.baseUrl ?? `…/d/${secret.deployment.slug}/v1`}
            </code>
          </div>
          <div>
            <label className="label text-xs">API-kulcs</label>
            <div className="flex items-center gap-2">
              <code className="block grow break-all rounded-md bg-slate-100 px-3 py-2 font-mono text-xs text-slate-700">
                {secret.apiKey}
              </code>
              <button type="button" className="btn-secondary shrink-0" onClick={copy}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Másolva' : 'Másol'}
              </button>
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button className="btn-primary" onClick={onClose}>Bezárás</button>
        </div>
      </div>
    </div>
  );
}
